import { MarginTypeResponse } from "../codegen-api";

export const normalizeMarginType = (
  marginType?: string | number | null
): MarginTypeResponse | undefined => {
  const normalized = String(marginType ?? "").toUpperCase();

  if (normalized === MarginTypeResponse.Isolated || normalized === "0") {
    return MarginTypeResponse.Isolated;
  }

  if (normalized === MarginTypeResponse.Cross || normalized === "1") {
    return MarginTypeResponse.Cross;
  }

  return undefined;
};

export const getMarginTypeLabel = (marginType?: string | number | null) => {
  const normalized = normalizeMarginType(marginType);

  if (normalized === MarginTypeResponse.Isolated) {
    return "Isolated";
  }

  if (normalized === MarginTypeResponse.Cross) {
    return "Cross";
  }

  return "-";
};

/**
 * On-chain `marginMode` encoding accepted by the perp router: 0 = ISOLATED,
 * 1 = CROSS. Mirrors the value signed into the order payload — see the
 * `marginMode` field in `usePerpOrder.place.ts`.
 */
export const PERP_MARGIN_MODE = {
  ISOLATED: 0,
  CROSS: 1,
} as const;

export type PerpMarginMode =
  (typeof PERP_MARGIN_MODE)[keyof typeof PERP_MARGIN_MODE];

/**
 * Translate a position's `margin_type` into the on-chain `marginMode` uint8.
 *
 * Returns `undefined` when the margin type is absent or unrecognised. Callers
 * MUST surface that rather than pick a side: `margin_type` is optional on the
 * API payload (`GetAccount200ResponsePositionsInner`), and the close path used
 * to fall through to CROSS whenever it was missing — silently submitting a
 * cross-margin close for what may have been an isolated position.
 */
export const toPerpMarginMode = (
  marginType?: string | number | null
): PerpMarginMode | undefined => {
  const normalized = normalizeMarginType(marginType);

  if (normalized === MarginTypeResponse.Isolated) {
    return PERP_MARGIN_MODE.ISOLATED;
  }

  if (normalized === MarginTypeResponse.Cross) {
    return PERP_MARGIN_MODE.CROSS;
  }

  return undefined;
};

/**
 * The inverse of `toPerpMarginMode`: the on-chain `marginMode` uint8 back to the
 * API's margin type.
 *
 * Exists because the order paths know the mode they SIGNED (`params.marginMode`)
 * but announce the resulting position to the trade-sync registry, which matches
 * on `margin_type`. Dropping it there made an open on one leg release the
 * pending close of the OTHER leg on the same market and side — `canBind` skips
 * the margin comparison when either side lacks it, so the open matched both.
 */
export const fromPerpMarginMode = (
  mode: number | undefined | null
): MarginTypeResponse | undefined => {
  if (mode === PERP_MARGIN_MODE.ISOLATED) return MarginTypeResponse.Isolated;
  if (mode === PERP_MARGIN_MODE.CROSS) return MarginTypeResponse.Cross;
  return undefined;
};
